import {
  PublicAffectedServiceItem,
  GroupedServiceGroup,
  ServiceGroup,
  ServiceItem,
  PublicIncidentItem,
  PublicIncidentUpdateItem,
  PublicMaintenanceItem
} from './status-main-page.models';
import {
  compareTimelineEntriesByTimestampDesc,
  parseTimestampFromSource
} from '../../shared/models/status-time.utils';

function toIsoTimestamp(source: any, additionalKeys: string[] = []): string | undefined {
  const parsed = parseTimestampFromSource(source, additionalKeys);
  return parsed ? parsed.toISOString() : undefined;
}

function toLocalTimestamp(value: any): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

function toTrimmedString(value: any): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

/**
 * Extracts collection items from API payloads.
 */
export function extractCollectionItems(response: any): any[] {
  if (Array.isArray(response?.data?.incidents)) {
    return response.data.incidents;
  }
  if (Array.isArray(response?.data?.maintenanceWindows)) {
    return response.data.maintenanceWindows;
  }
  if (Array.isArray(response?.data?.items)) {
    return response.data.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.incidents)) {
    return response.incidents;
  }
  if (Array.isArray(response?.maintenanceWindows)) {
    return response.maintenanceWindows;
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
}

/**
 * Normalizes incidents into a consistent internal format.
 */
export function normalizeIncidents(response: any): PublicIncidentItem[] {
  const list = extractCollectionItems(response);
  const responseLevelUpdates = indexIncidentUpdatesByIncidentId(response);
  return list.map((item: any) => {
    const incidentId = toNumberOrUndefined(item?.incidentId ?? item?.incident_id ?? item?.id);
    const incidentLookupKey = resolveLookupKey(item?.incidentId || item?.incident_id || item?.id);
    const fallbackUpdates = incidentLookupKey ? responseLevelUpdates.get(incidentLookupKey) || [] : [];
    const hasPostmortem = toBooleanOrUndefined(
      item?.hasPostmortem ??
        item?.has_postmortem ??
        item?.hasPostMortem ??
        item?.postmortemAvailable ??
        item?.postmortem_available
    );
    const isIncident = toBooleanOrUndefined(
      item?.incident ??
        item?.isIncident ??
        item?.is_incident
    );

    const affectedServices = normalizeAffectedServices(item);
    const affectedServiceIds = resolveAffectedServiceIds(item, affectedServices);
    const affectedServiceNames = resolveAffectedServiceNames(item, affectedServices);

    return {
      incidentId,
      title: item?.title || item?.name,
      description: item?.description || item?.message,
      status: item?.status ?? item?.statusCode ?? item?.status_code,
      statusName: item?.statusName || item?.status_name || resolveIncidentStatusName(item?.status ?? item?.status_code),
      isActive: item?.isActive ?? item?.is_active,
      startedAt: toIsoTimestamp(item, [
        'startedAt',
        'started_at',
        'createdDate',
        'createdAt',
        'created_at',
        'created_date'
      ]),
      resolvedAt: toIsoTimestamp(item, [
        'resolvedAt',
        'resolved_at',
        'closedAt',
        'closed_at',
        'endedAt',
        'ended_at'
      ]),
      startedAtLocal: toLocalTimestamp(item?.startedAtLocal ?? item?.started_at_local),
      resolvedAtLocal: toLocalTimestamp(item?.resolvedAtLocal ?? item?.resolved_at_local),
      timezoneAbbreviation: toTrimmedString(item?.timezoneAbbreviation ?? item?.timezone_abbreviation),
      durationMinutes: toNumberOrUndefined(item?.durationMinutes ?? item?.duration_minutes),
      impact: toNumberOrUndefined(item?.impact ?? item?.impactCode ?? item?.impact_code) ?? null,
      impactName: toTrimmedString(item?.impactName ?? item?.impact_name),
      affectedServiceIds,
      affectedServices,
      affectedServiceNames,
      updates: normalizeIncidentUpdates(item, fallbackUpdates),
      hasPostmortem,
      isIncident
    };
  });
}

/**
 * Normalizes incident updates into a consistent internal format.
 */
function normalizeIncidentUpdates(item: any, fallbackUpdates: any[] = []): PublicIncidentUpdateItem[] {
  let updatesSource: any[] = [];
  if (Array.isArray(item?.updates)) {
    updatesSource = item.updates;
  } else if (Array.isArray(item?.timeline)) {
    updatesSource = item.timeline;
  } else if (Array.isArray(item?.incidentUpdates)) {
    updatesSource = item.incidentUpdates;
  } else if (Array.isArray(item?.incident_updates)) {
    updatesSource = item.incident_updates;
  }
  const mergedUpdates = [...updatesSource, ...fallbackUpdates];
  const rawUpdates = mergedUpdates.filter((update: any) => (
    !!update &&
    update?.displayToPublic !== false &&
    update?.display_to_public !== false
  ));

  const normalized = dedupeIncidentUpdates(rawUpdates.map((update: any) => ({
    incidentUpdateId: toNumberOrUndefined(
      update?.incidentUpdateId ||
      update?.incident_update_id ||
      update?.updateId ||
      update?.update_id ||
      update?.id
    ),
    status: update?.status ?? update?.statusCode ?? update?.status_code,
    statusName:
      update?.statusName ||
      update?.status_name ||
      update?.statusLabel ||
      update?.status_label ||
      resolveIncidentStatusName(update?.status ?? update?.status_code),
    message: update?.message || update?.description || update?.content || update?.body,
    timestamp: toIsoTimestamp(update),
    createdDateLocal: toLocalTimestamp(update?.createdDateLocal ?? update?.created_date_local),
    timezoneAbbreviation: toTrimmedString(update?.timezoneAbbreviation ?? update?.timezone_abbreviation)
  })));

  return normalized.sort((a, b) =>
    compareTimelineEntriesByTimestampDesc(
      a,
      b,
      (entry) => entry?.timestamp,
      (entry) => entry?.incidentUpdateId
    )
  );
}

/**
 * Removes duplicate incident updates while keeping the first occurrence.
 */
function dedupeIncidentUpdates(updates: PublicIncidentUpdateItem[]): PublicIncidentUpdateItem[] {
  const seen = new Set<string>();
  return updates.filter((update) => {
    const key = [
      String(update?.incidentUpdateId || ''),
      String(update?.timestamp || ''),
      String(update?.statusName || update?.status || ''),
      String(update?.message || '').trim()
    ].join('|');

    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * Extracts response incident updates from API payloads.
 */
function extractResponseIncidentUpdates(response: any): any[] {
  if (Array.isArray(response?.updates)) {
    return response.updates;
  }
  if (Array.isArray(response?.incidentUpdates)) {
    return response.incidentUpdates;
  }
  if (Array.isArray(response?.incident_updates)) {
    return response.incident_updates;
  }
  if (Array.isArray(response?.data?.updates)) {
    return response.data.updates;
  }
  if (Array.isArray(response?.data?.incidentUpdates)) {
    return response.data.incidentUpdates;
  }
  if (Array.isArray(response?.data?.incident_updates)) {
    return response.data.incident_updates;
  }
  return [];
}

/**
 * Indexes incident updates by incident id for efficient lookup operations.
 */
function indexIncidentUpdatesByIncidentId(response: any): Map<string, any[]> {
  const lookup = new Map<string, any[]>();
  const updates = extractResponseIncidentUpdates(response);

  updates.forEach((update: any) => {
    const incidentKey = resolveLookupKey(
      update?.incidentId ||
      update?.incident_id ||
      update?.parentIncidentId ||
      update?.parent_incident_id ||
      update?.incident?.incidentId ||
      update?.incident?.incident_id
    );

    if (!incidentKey) {
      return;
    }

    const existing = lookup.get(incidentKey) || [];
    existing.push(update);
    lookup.set(incidentKey, existing);
  });

  return lookup;
}

/**
 * Resolves lookup key using fallback rules.
 */
function resolveLookupKey(value: any): string {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
}

/**
 * Converts number or undefined to the expected output format.
 */
function toNumberOrUndefined(value: any): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Converts boolean or undefined to the expected output format.
 */
function toBooleanOrUndefined(value: any): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 1 || value === 0) {
    return value === 1;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return undefined;
}

/**
 * Normalizes affected services into a consistent internal format.
 */
function normalizeAffectedServices(item: any): PublicAffectedServiceItem[] {
  let rawServices: any[] = [];
  if (Array.isArray(item?.affectedServices)) {
    rawServices = item.affectedServices;
  } else if (Array.isArray(item?.affected_services)) {
    rawServices = item.affected_services;
  } else if (Array.isArray(item?.services)) {
    rawServices = item.services;
  }

  return rawServices.map((service: any) => ({
    serviceId: Number(service?.serviceId || service?.service_id || service?.id) || undefined,
    serviceName: service?.serviceName || service?.service_name || service?.name,
    serviceStatus: service?.serviceStatus ?? service?.service_status ?? service?.status,
    serviceStatusName: service?.serviceStatusName || service?.service_status_name || service?.statusName || service?.status_name
  }));
}

/**
 * Resolves incident status name using fallback rules.
 */
function resolveIncidentStatusName(status: any): string {
  if (typeof status === 'string' && status.trim()) {
    return status;
  }
  const statusMap: Record<number, string> = {
    1: 'Investigating',
    2: 'Identified',
    3: 'Monitoring',
    4: 'Resolved'
  };
  const statusNumber = Number(status);
  if (Number.isFinite(statusNumber) && statusMap[statusNumber]) {
    return statusMap[statusNumber];
  }
  return '';
}

/**
 * Normalizes maintenances into a consistent internal format.
 */
export function normalizeMaintenances(response: any): PublicMaintenanceItem[] {
  const list = extractCollectionItems(response);
  return list.map((item: any) => {
    const affectedServices = normalizeAffectedServices(item);
    const affectedServiceIds = resolveAffectedServiceIds(item, affectedServices);
    const affectedServiceNames = resolveAffectedServiceNames(item, affectedServices);

    return {
      maintenanceWindowId: item?.maintenanceWindowId ?? item?.maintenance_window_id ?? item?.id,
      title: item?.title,
      description: item?.description,
      status: item?.status,
      statusName: item?.statusName,
      isActive: item?.isActive,
      isCompleted: item?.isCompleted,
      isUpcoming: item?.isUpcoming,
      scheduledStartDate: toIsoTimestamp(item, [
        'scheduledStartDate',
        'scheduled_start_date',
        'startDate',
        'start_date'
      ]),
      scheduledEndDate: toIsoTimestamp(item, [
        'scheduledEndDate',
        'scheduled_end_date',
        'endDate',
        'end_date'
      ]),
      actualStartDate: toIsoTimestamp(item, ['actualStartDate', 'actual_start_date']),
      actualEndDate: toIsoTimestamp(item, ['actualEndDate', 'actual_end_date']),
      scheduledStartDateLocal: toLocalTimestamp(item?.scheduledStartDateLocal ?? item?.scheduled_start_date_local),
      scheduledEndDateLocal: toLocalTimestamp(item?.scheduledEndDateLocal ?? item?.scheduled_end_date_local),
      actualStartDateLocal: toLocalTimestamp(item?.actualStartDateLocal ?? item?.actual_start_date_local),
      actualEndDateLocal: toLocalTimestamp(item?.actualEndDateLocal ?? item?.actual_end_date_local),
      timezoneAbbreviation: toTrimmedString(item?.timezoneAbbreviation ?? item?.timezone_abbreviation),
      durationMinutes: item?.durationMinutes,
      affectedServiceIds,
      affectedServices,
      affectedServiceNames
    };
  });
}

/**
 * Normalizes grouped services into a consistent internal format.
 */
export function normalizeGroupedServices(response: any): GroupedServiceGroup[] {
  const groups = Array.isArray(response?.groups)
    ? response.groups
    : Array.isArray(response?.data?.groups)
      ? response.data.groups
      : [];

  return groups
    .map((group: any) => {
      const rawServices = Array.isArray(group?.services) ? group.services : [];
      const services = rawServices
        .filter((service: any) => service?.hasAccess !== false)
        .map((service: any) => mapGroupedServiceItem(service))
        .sort((a, b) => compareByDisplayOrder(a?.displayOrder, b?.displayOrder, a?.name, b?.name));

      if (!services.length) {
        return null;
      }

      const groupId = toNumberOrUndefined(group?.groupId ?? group?.group_id ?? group?.id);
      return {
        groupId: groupId ?? 0,
        name: String(group?.name ?? group?.groupName ?? ''),
        description: group?.description ?? null,
        displayOrder: toNumberOrUndefined(group?.displayOrder ?? group?.display_order) ?? Number.MAX_SAFE_INTEGER,
        effectiveStatus: String(group?.effectiveStatus ?? group?.effective_status ?? ''),
        effectiveStatusCode: toNumberOrUndefined(group?.effectiveStatusCode ?? group?.effective_status_code) ?? 0,
        services,
        expanded: false
      } as GroupedServiceGroup;
    })
    .filter((group): group is GroupedServiceGroup => !!group)
    .sort((a, b) => compareByDisplayOrder(a.displayOrder, b.displayOrder, a.name, b.name));
}

/**
 * Normalizes ungrouped services from the grouped services response.
 */
export function normalizeUngroupedServices(response: any): ServiceItem[] {
  const ungroupedServices = Array.isArray(response?.ungroupedServices)
    ? response.ungroupedServices
    : Array.isArray(response?.data?.ungroupedServices)
      ? response.data.ungroupedServices
      : [];

  return ungroupedServices
    .filter((service: any) => service?.hasAccess !== false)
    .map((service: any) => mapGroupedServiceItem(service))
    .sort((a, b) => compareByDisplayOrder(a?.displayOrder, b?.displayOrder, a?.name, b?.name));
}

/**
 * Normalizes the complete grouped-services response into a unique service list.
 */
export function normalizeGroupedServiceItems(response: any): ServiceItem[] {
  const allServices = new Map<number, ServiceItem>();

  const addService = (service: ServiceItem): void => {
    const serviceId = Number(service?.serviceId);
    if (!Number.isFinite(serviceId) || serviceId <= 0) {
      return;
    }

    const existing = allServices.get(serviceId);
    if (!existing) {
      allServices.set(serviceId, service);
      return;
    }

    existing.displayOrder = Math.min(existing.displayOrder, service.displayOrder);
    if (!existing.description && service.description) {
      existing.description = service.description;
    }
    if (!existing.statusMessage && service.statusMessage) {
      existing.statusMessage = service.statusMessage;
    }
    if (service.groups?.length && !existing.groups?.length) {
      existing.groups = service.groups;
    }
  };

  normalizeGroupedServices(response).forEach((group) => {
    (group?.services || []).forEach((service) => addService(service));
  });
  normalizeUngroupedServices(response).forEach((service) => addService(service));

  return Array.from(allServices.values())
    .sort((a, b) => compareByDisplayOrder(a?.displayOrder, b?.displayOrder, a?.name, b?.name));
}

function mapGroupedServiceItem(item: any): ServiceItem {
  const groups = Array.isArray(item?.groups) ? item.groups : [];
  return {
    serviceId: toNumberOrUndefined(item?.serviceId ?? item?.service_id ?? item?.id) ?? 0,
    name: String(item?.name ?? ''),
    description: String(item?.description ?? ''),
    effectiveStatus: String(item?.effectiveStatus ?? item?.effective_status ?? ''),
    effectiveStatusCode: toNumberOrUndefined(item?.effectiveStatusCode ?? item?.effective_status_code) ?? 0,
    statusMessage: item?.statusMessage ?? item?.status_message ?? null,
    displayOrder: toNumberOrUndefined(item?.displayOrder ?? item?.display_order) ?? Number.MAX_SAFE_INTEGER,
    groups: groups.map((entry: any) => ({
      groupId: toNumberOrUndefined(entry?.groupId ?? entry?.group_id ?? entry?.id) ?? 0,
      name: String(entry?.name ?? entry?.groupName ?? '')
    })) as ServiceGroup[],
    hasActiveOverride: item?.hasActiveOverride ?? item?.has_active_override ?? false,
    activeOverride: item?.activeOverride ?? item?.active_override ?? null
  };
}

function compareByDisplayOrder(
  leftOrder: number | undefined,
  rightOrder: number | undefined,
  leftName?: string,
  rightName?: string
): number {
  const left = Number.isFinite(Number(leftOrder)) ? Number(leftOrder) : Number.MAX_SAFE_INTEGER;
  const right = Number.isFinite(Number(rightOrder)) ? Number(rightOrder) : Number.MAX_SAFE_INTEGER;
  if (left !== right) {
    return left - right;
  }
  return String(leftName || '').localeCompare(String(rightName || ''));
}

/**
 * Maps status class into UI-friendly output.
 */
export function mapStatusClass(status: string): string {
  switch (String(status || '').toLowerCase()) {
    case 'operational':
      return 'operational';
    case 'degraded':
    case 'degraded performance':
      return 'degraded';
    case 'partial outage':
      return 'partial';
    case 'major outage':
      return 'major';
    case 'under maintenance':
    case 'scheduled maintenance':
      return 'maintenance';
    default:
      return 'unknown';
  }
}

/**
 * Maps maintenance status label into UI-friendly output.
 */
export function mapMaintenanceStatusLabel(item: PublicMaintenanceItem): string {
  let fallback = 'Scheduled';
  if (item?.isCompleted) {
    fallback = 'Completed';
  } else if (item?.isActive) {
    fallback = 'In Progress';
  }
  return String(item?.statusName || item?.status || fallback);
}

/**
 * Maps incident status label into UI-friendly output.
 */
export function mapIncidentStatusLabel(item: PublicIncidentItem): string {
  const resolvedStatus = resolveIncidentStatusName(item?.status);
  const fallback = item?.isActive ? 'Active' : 'Resolved';
  return String(item?.statusName || resolvedStatus || fallback);
}

/**
 * Resolves affected service ids using fallback rules.
 */
function resolveAffectedServiceIds(
  item: any,
  affectedServices: PublicAffectedServiceItem[]
): number[] {
  if (Array.isArray(item?.affectedServiceIds)) {
    return item.affectedServiceIds;
  }
  if (Array.isArray(item?.affected_service_ids)) {
    return item.affected_service_ids;
  }
  return affectedServices
    .map((service) => Number(service?.serviceId))
    .filter((serviceId) => Number.isFinite(serviceId));
}

/**
 * Resolves affected service names using fallback rules.
 */
function resolveAffectedServiceNames(
  item: any,
  affectedServices: PublicAffectedServiceItem[]
): string[] {
  if (Array.isArray(item?.affectedServiceNames)) {
    return item.affectedServiceNames.filter((name: any) => !!String(name || '').trim());
  }
  if (Array.isArray(item?.affected_service_names)) {
    return item.affected_service_names.filter((name: any) => !!String(name || '').trim());
  }
  return affectedServices
    .map((service) => String(service?.serviceName || '').trim())
    .filter((name) => !!name);
}
